import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { CheckCircle, Warning, ArrowClockwise, ShieldCheck } from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * Admin-only widget on the Dashboard that at-a-glance shows whether THIS
 * environment's demo accounts are in a healthy state — i.e., whether
 * Kory's expected passwords still verify against the current password
 * hashes. If prod is out-of-sync with dev, this widget shows a red bar
 * with a one-click "Restore Kory's passwords" button.
 *
 * Existed because the same "prod DB stale" issue burned an entire cycle
 * on Feb 5, 2026. Now it's visible.
 */
export default function DemoAccountHealthWidget() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/demo/health')
      .then((r) => setHealth(r.data))
      .catch(() => setHealth(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const onRestore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      await api.post('/admin/demo/restore-kory-passwords');
      toast.success("Kory's passwords restored. Reloading health…");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) return null;
  if (!health) return null;

  const envHealthy = health.env_healthy;
  const accounts = health.accounts || {};

  return (
    <div
      className={`border rounded-sm mb-4 ${
        envHealthy ? 'border-emerald-300 bg-emerald-50' : 'border-red-400 bg-red-50'
      }`}
      data-testid="demo-account-health-widget"
    >
      <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck
            size={18}
            weight="bold"
            className={envHealthy ? 'text-emerald-600' : 'text-red-600'}
          />
          <h3
            className={`text-sm font-semibold uppercase tracking-wide ${
              envHealthy ? 'text-emerald-800' : 'text-red-800'
            }`}
          >
            Demo Account Health
          </h3>
          <span
            className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
              envHealthy
                ? 'bg-emerald-600 text-white'
                : 'bg-red-600 text-white animate-pulse'
            }`}
            data-testid="demo-health-status-pill"
          >
            {envHealthy ? 'Healthy' : 'Drift detected'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#6B7280] font-mono">
            v{health.app_version}
          </span>
          {!envHealthy && (
            <button
              type="button"
              onClick={onRestore}
              disabled={restoring}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
              data-testid="demo-health-restore-btn"
            >
              <ArrowClockwise size={12} weight="bold" className={restoring ? 'animate-spin' : ''} />
              {restoring ? 'Restoring…' : "Restore Kory's passwords"}
            </button>
          )}
          {envHealthy && (
            <button
              type="button"
              onClick={load}
              className="text-[10px] text-[#6B7280] hover:text-emerald-700 flex items-center gap-1"
              data-testid="demo-health-refresh-btn"
            >
              <ArrowClockwise size={11} weight="bold" />
              Refresh
            </button>
          )}
        </div>
      </div>

      {!envHealthy && (
        <div className="px-4 py-2 text-[11px] text-red-800 bg-red-100/60 border-b border-red-200">
          One or both demo accounts don&rsquo;t match Kory&rsquo;s expected passwords
          on this environment. Click <strong>Restore Kory&rsquo;s passwords</strong>{' '}
          to sync them &mdash; idempotent, safe to run anytime.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        {Object.entries(accounts).map(([email, acc], idx) => {
          const status = !acc.exists
            ? { color: 'text-red-700', label: 'Missing', bg: 'bg-red-100' }
            : acc.disabled
            ? { color: 'text-red-700', label: 'Disabled', bg: 'bg-red-100' }
            : !acc.kory_password_ok
            ? { color: 'text-red-700', label: 'Password stale', bg: 'bg-red-100' }
            : { color: 'text-emerald-700', label: "Kory's password OK", bg: 'bg-emerald-100' };
          return (
            <div
              key={email}
              className={`px-4 py-3 ${idx === 0 ? 'md:border-r border-black/5' : ''}`}
              data-testid={`demo-health-account-${idx}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <code className="text-[11px] font-mono text-[#111827] truncate" title={email}>
                  {email}
                </code>
                <span
                  className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${status.bg} ${status.color} whitespace-nowrap`}
                >
                  {acc.kory_password_ok && !acc.disabled && acc.exists ? (
                    <CheckCircle size={10} weight="fill" className="inline mr-1 mb-0.5" />
                  ) : (
                    <Warning size={10} weight="fill" className="inline mr-1 mb-0.5" />
                  )}
                  {status.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-[#4B5563]">
                <span>Role: <span className="font-mono text-[#111827]">{acc.role || '—'}</span></span>
                <span>Hash: <span className="font-mono text-[#111827]">{acc.hash_prefix || '—'}</span></span>
                <span>Attempts: <span className="font-mono text-[#111827]">{acc.failed_login_attempts ?? 0}</span></span>
                <span>Locked: <span className="font-mono text-[#111827]">{acc.locked_until ? 'yes' : 'no'}</span></span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
